// <size-finder> (§7.1): pide peso, pecho, cintura y sexo; recomienda talla con recommendSize (size-logic.js).
// Modo diálogo (ficha) o página (guía de tallas). La banda del campo enfocado se ilumina en la ilustración.
// Las medidas se guardan solo en sessionStorage (se borran al cerrar la pestaña) y nunca van a la analítica.
import { recommendSize, recommendSockSize } from './size-logic.js';

const STORE_KEY = 'pawly-medidas';
const FIELDS = ['weight', 'chest', 'waist'];

function el(tag, attrs = {}, ...children) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v;
    else node.setAttribute(k, v);
  }
  node.append(...children.filter((c) => c != null));
  return node;
}

class SizeFinder extends HTMLElement {
  connectedCallback() {
    this.config = JSON.parse(this.querySelector('[data-size-finder-config]').textContent);
    this.s = this.config.strings;
    this.form = this.querySelector('.size-finder__form');
    this.result = this.querySelector('.size-finder__result');
    this.summary = this.querySelector('.size-finder__summary');
    this.measure = this.querySelector('.measure') ?? (this.dataset.mode === 'page' ? document.querySelector('.size-guide__illustration .measure') : null);
    this.dialog = this.closest('dialog');
    this.handle = this.dataset.productHandle;
    this.kind = this.config.kind ?? 'harness';
    // Una sola pieza trasera para los dos sexos: no se pregunta el sexo. Tabla sin cintura: no se pide la cintura.
    this.askSex = this.kind !== 'socks' && this.config.askSex !== false;
    this.fields = this.kind === 'socks' ? ['width'] : FIELDS.filter((f) => (f !== 'waist' || this.config.askWaist !== false) && (f !== 'weight' || this.config.askWeight !== false));

    this.form.addEventListener('focusin', (event) => {
      if (!this.measure) return;
      const band = event.target.closest('[data-band-field]')?.dataset.bandField;
      if (band) this.measure.dataset.focus = band;
      else delete this.measure.dataset.focus;
    });
    this.form.addEventListener('focusout', () => setTimeout(() => {
      if (this.measure && !this.form.contains(document.activeElement)) delete this.measure.dataset.focus;
    }));
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.evaluate();
    });
    this.restore();

    if (this.dialog) {
      const section = this.dialog.closest('[data-section]') ?? document;
      section.querySelectorAll('[data-open-size-finder]').forEach((opener) => opener.addEventListener('click', () => this.open(opener)));
      this.dialog.querySelector('[data-close-dialog]')?.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => this.opener?.focus());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
    }
  }

  open(opener) {
    this.opener = opener;
    this.dialog.showModal();
    this.form.querySelector('input')?.focus();
    window.Shopify?.analytics?.publish?.('pawly:select_size_finder', { product_handle: this.handle, location: opener.dataset.analyticsLocation ?? 'pdp' });
  }

  input() {
    const value = (name) => this.form.elements[name]?.value ?? '';
    if (this.kind === 'socks') return { width: value('width') };
    return { weight: value('weight'), chest: value('chest'), waist: value('waist'), sex: this.form.querySelector('input[name="sex"]:checked')?.value };
  }

  restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORE_KEY));
      if (!saved) return;
      for (const name of this.fields) if (saved[name] && this.form.elements[name]) this.form.elements[name].value = saved[name];
      if (saved.sex && this.kind !== 'socks') this.form.querySelector(`input[name="sex"][value="${saved.sex}"]`)?.click();
    } catch {
      // Sin sessionStorage: no pasa nada, se escriben de nuevo.
    }
  }

  errorMessage(field, code) {
    if (field === 'sex') return this.s.error_sex;
    return code === 'range' ? this.s[`error_${field}_range`] : this.s[`error_${field}`];
  }

  showErrors(errors) {
    const fields = this.kind === 'socks' ? ['width'] : [...this.fields, ...(this.askSex ? ['sex'] : [])];
    const list = el('ul');
    for (const field of fields) {
      const control = field === 'sex' ? this.form.querySelector('.size-finder__sex') : this.form.elements[field];
      const error = this.querySelector(`#finder-${field}-error-${this.handle}`);
      if (errors[field]) {
        const message = this.errorMessage(field, errors[field]);
        error.querySelector('span').textContent = message;
        error.removeAttribute('hidden');
        if (field !== 'sex') control.setAttribute('aria-invalid', 'true');
        const target = field === 'sex' ? this.form.querySelector('input[name="sex"]') : control;
        const link = el('a', { href: `#${target.id || ''}` }, message);
        link.addEventListener('click', (event) => {
          event.preventDefault();
          target.focus();
        });
        list.append(el('li', {}, link));
      } else {
        error.setAttribute('hidden', '');
        if (field !== 'sex') control.removeAttribute('aria-invalid');
      }
    }
    if (list.children.length) {
      this.summary.replaceChildren(el('p', { class: 'size-finder__summary-title' }, this.s.errors_title), list);
      this.summary.removeAttribute('hidden');
      this.summary.focus();
    } else {
      this.summary.setAttribute('hidden', '');
    }
  }

  evaluate() {
    const input = this.input();
    const r = this.kind === 'socks' ? recommendSockSize(this.config.rows, input) : recommendSize(this.config.rows, input, { askSex: this.askSex });
    if (r.type === 'invalid') {
      this.showErrors(r.errors);
      return;
    }
    this.showErrors({});
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORE_KEY) ?? '{}');
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ ...saved, ...input }));
    } catch {
      // Sin sessionStorage: el formulario de ayuda no se rellenará solo.
    }
    this.result.replaceChildren(this.render(r));
    this.result.scrollIntoView({ block: 'nearest', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    window.Shopify?.analytics?.publish?.('pawly:size_result', { result_type: r.type, size: r.size ?? null, sex: r.sex ?? null, data_status: this.config.dataStatus });
  }

  render(r) {
    const s = this.s;
    const box = el('div', { class: `size-finder__outcome size-finder__outcome--${r.type}` });
    const sexWord = r.sex === 'macho' ? s.male_lower : s.female_lower;
    if (r.type === 'match') {
      // Tabla sin cintura: el titular no puede decir que «pecho y cintura» caen en su rango.
      const harnessMatch = this.fields.includes('waist') ? s.match : (s.match_chest ?? s.match);
      const headline = this.kind === 'socks' ? s.sock_match.replace('{{ size }}', r.size) : harnessMatch.replace('{{ size }}', r.size).replace('{{ sex }}', sexWord);
      box.append(el('p', { class: 'size-finder__headline' }, headline));
      const notes = r.notes.map((note) => (note.code === 'weight' ? s.weight_note : s[note.measure === 'pecho' ? 'edge_chest' : 'edge_waist'].replace('{{ edge }}', note.edge)));
      if (notes.length) box.append(el('ul', { class: 'size-finder__notes' }, ...notes.map((n) => el('li', {}, n))));
      if (this.dialog) {
        const choose = el('button', { type: 'button', class: 'button button--primary' }, s.choose);
        choose.addEventListener('click', () => {
          this.dialog.close();
          document.dispatchEvent(new CustomEvent('size-finder:choose', { detail: { productHandle: this.handle, size: r.size, sex: r.sex } }));
        });
        box.append(choose);
      } else if (this.config.productUrl) {
        const variant = this.config.variants?.find((v) => v.options[0] === r.size && (this.kind === 'socks' || !this.askSex || (v.options[1] ?? '').toLowerCase() === r.sex));
        box.append(el('a', { class: 'button button--primary', href: variant ? `${this.config.productUrl}?variant=${variant.id}` : this.config.productUrl }, s.choose));
      }
    } else if (r.type === 'split') {
      box.append(el('p', { class: 'size-finder__headline' }, s.split));
      box.append(el('p', {}, s.split_detail.replace('{{ chest }}', r.chestSize).replace('{{ waist }}', r.waistSize)));
      box.append(el('a', { class: 'button button--primary', href: this.config.helpUrl }, s.send_measures));
    } else if (r.type === 'between') {
      const rule = el('p', { class: 'size-finder__headline' });
      rule.innerHTML = this.config.ruleBetween; // HTML de confianza: lo pinta el servidor (texto del fabricante o marcador).
      box.append(rule, el('a', { class: 'button button--primary', href: this.config.helpUrl }, s.send_measures));
    } else {
      box.append(el('p', { class: 'size-finder__headline' }, s.none), el('a', { class: 'button button--primary', href: this.config.contactUrl }, s.contact));
    }
    if (this.kind !== 'socks') box.append(el('p', { class: 'size-finder__fit text-small' }, s.fit_check));
    box.append(el('p', { class: 'text-small' }, el('a', { href: this.config.helpUrl }, s.human_check)));
    return box;
  }
}

customElements.define('size-finder', SizeFinder);
